import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-solid-svg-icons'

import { ActiveFilterViewer, Filters } from '@rankmi/rankmi-table'
import { useTranslation } from 'react-i18next'
import TableSegments from 'components/ui/TableSegments'
import { useSegmentsColumns } from 'components/configuration/components/lms-profiles/common/hooks'
import { UnArray } from 'helpers/types.helper'
import useSegmentColumnsFilters from 'hooks/users/useSegmentColumnsFilters'
import { Segment } from 'models/segment.model'
import { SegmentModal } from '../../segments'
import { TableProps } from './types'
import { Actions } from './styled'

const SegmentsTable = ({
  onAllRowKeys,
  data: { loading, list, total },
  onRowSelected,
  onRemove,
  selected,
  paginationConfig: {
    setSort,
    page,
    setPage,
    perPage,
    setPerPage,
    filterData,
    onChangeFilterData,
    search,
    onSearch,
  },
  actions,
}: TableProps) => {
  const { t } = useTranslation()
  const { filtersColumns } = useSegmentColumnsFilters()

  const [showFiltersModal, setShowFiltersModal] = useState(false)

  const dataSource = useMemo(
    () =>
      list.map((user) => ({
        key: user.id,
        ...user,
      })),
    [list]
  )

  const [segment, setSegment] = useState<Segment>()

  const onOpenSegment = useCallback((segment: Segment) => {
    setSegment(segment)
  }, [])

  const baseColumns = useSegmentsColumns()
  const columns = useMemo(
    () => [
      ...baseColumns,
      {
        title: '',
        key: 'action',
        hideable: false,
        width: 100,
        render: (_, participant: UnArray<typeof dataSource>) => (
          <Actions className="d-flex justify-content-end">
            <Button onClick={() => onOpenSegment?.(participant)}>
              {t('configuration.segments.common.viewSegment')}
            </Button>
            <Button onClick={() => onRemove(participant)}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Actions>
        ),
      },
    ],
    [baseColumns, onRemove, onOpenSegment, t]
  )

  const handleAllSelectedRef = useRef(onAllRowKeys)

  useEffect(() => {
    handleAllSelectedRef.current = onAllRowKeys
  }, [onAllRowKeys])

  return (
    <>
      <div className="row">
        <div className="col">
          <Filters
            columns={filtersColumns}
            onChange={(value) => {
              onChangeFilterData(value)
            }}
            open={showFiltersModal}
            onVisibilityChange={(state: boolean) => {
              setShowFiltersModal(state)
            }}
            selectedFilters={filterData}
          />
        </div>
        <div className="col-auto">{actions}</div>
      </div>
      {filterData && (
        <div className="mt-4">
          <ActiveFilterViewer
            activeFilters={filterData}
            onChange={(data) => onChangeFilterData(data)}
            openFilters={(state: boolean) => setShowFiltersModal(state)}
            columns={filtersColumns}
          />
        </div>
      )}
      <div>
        <TableSegments
          changeColumnsVisibility
          responsive
          data={dataSource}
          search={search}
          currentPage={page}
          initialRowsPerPage={perPage}
          delaySearch
          onSearch={onSearch}
          total={total}
          columnHeaders={columns}
          onSortChange={(sortData) => setSort(sortData)}
          onRowSelected={onRowSelected}
          selectedRows={selected}
          onPaginationChange={(currentPage: number) => setPage(currentPage)}
          simpleSelectAll={true}
          onRowsPerPageChange={(itemsPerPage: number) =>
            setPerPage(itemsPerPage)
          }
          onAllRowKeys={
            onAllRowKeys
              ? () => {
                  // Rankmi-table has a bug, use onAllRowKeys how reference
                  handleAllSelectedRef.current?.()
                }
              : undefined
          }
          fastActions={[]}
          loading={loading}
        />
      </div>
      <SegmentModal
        open={!!segment}
        onClose={() => {
          setSegment(undefined)
        }}
        segment={segment}
      />
    </>
  )
}

export default SegmentsTable
